import store, { AppState } from '../../state';
import { RecentClimateTimeMode, RecentClimateVariable } from '../../types';
import {
  RECENT_CLIMATE_METRICS,
  RECENT_CLIMATE_MONTH_NAMES,
  RecentClimateService
} from '../../services/RecentClimateService';

export class RecentClimatePanel {
  private container: HTMLElement | null = null;
  public onClose?: () => void;

  public setOnClose(cb: () => void): void {
    this.onClose = cb;
  }

  public render(state: AppState): string {
    const isImperial = state.units !== 'metric';
    const years = RecentClimateService.getAvailableYears(state.manifest);
    const sel = RecentClimateService.resolveSelection(
      state.manifest,
      state.recentClimateYear,
      state.recentClimateTimeMode,
      state.recentClimateMonth,
      state.recentClimateVariable
    );
    const { year, yearMeta, timeMode, month, variable } = sel;
    const availMonths = new Set<number>(yearMeta.months || []);
    const isComplete = !!yearMeta.is_complete_year;

    const ytdButtonLabel = isComplete
      ? `Full Year ${year} (Annual)`
      : `${year} Year-to-Date (YTD)`;

    const statusBadge = isComplete
      ? 'Full Year (Jan–Dec)'
      : (yearMeta.as_of_date ? `Thru ${yearMeta.as_of_date}` : `${year} YTD`);

    const activeSpec = RecentClimateService.getMetricSpec(variable);
    const activeLayerTitle = timeMode === 'month'
      ? `${activeSpec.monthTitle} • ${RECENT_CLIMATE_MONTH_NAMES[month - 1]} ${year}`
      : (isComplete ? `${activeSpec.annualTitle} • ${year}` : `${activeSpec.ytdTitle} • ${year}`);

    const legend = RecentClimateService.getLegendConfig(variable, timeMode, isImperial);

    return `
      <div class="gw-recent-climate-panel" style="display: flex; flex-direction: column; gap: 8px; padding: 2px 0; font-family: inherit;">
        <style>
          .gw-rc-year-select {
            min-width: 96px;
            padding: 4px 8px;
            font-size: 0.76rem;
            font-weight: 700;
            color: #f8fafc;
            background: rgba(15, 23, 42, 0.9);
            border: 1px solid rgba(56, 189, 248, 0.4);
            border-radius: 6px;
            cursor: pointer;
            outline: none;
          }
          @supports (appearance: base-select) {
            .gw-rc-year-select,
            .gw-rc-year-select::picker(select) {
              appearance: base-select;
            }
            .gw-rc-year-select::picker(select) {
              background: #0f172a;
              color: #f8fafc;
              border: 1px solid rgba(56, 189, 248, 0.45);
              border-radius: 8px;
              padding: 4px;
            }
            .gw-rc-year-select::picker-icon {
              color: #38bdf8;
            }
            .gw-rc-year-select option {
              padding: 5px 8px;
              border-radius: 5px;
              color: #f8fafc;
              cursor: pointer;
            }
            .gw-rc-year-select option:hover {
              background: rgba(56, 189, 248, 0.2);
            }
            .gw-rc-year-select option::checkmark {
              color: #38bdf8;
            }
          }
          .gw-rc-year-select:focus-visible {
            border-color: #38bdf8;
            box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.25);
          }
          .gw-rc-month-btn:disabled {
            opacity: 0.32;
            cursor: not-allowed;
          }
        </style>

        <!-- Year Selector & Observation Coverage Badge -->
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; background: rgba(15, 23, 42, 0.65); border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 7px; padding: 6px 8px;">
          <div style="display: flex; align-items: center; gap: 7px;">
            <label for="gw-rc-year-select" style="font-size: 0.70rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">Year</label>
            <select id="gw-rc-year-select" name="recent_climate_year" class="gw-rc-year-select" aria-label="Select climate observation year">
              ${years.map((yr) => {
                const ym = RecentClimateService.getYearMeta(state.manifest, yr);
                const suffix = ym.is_complete_year ? '' : ' (YTD)';
                return `<option value="${yr}" ${yr === year ? 'selected' : ''}>${yr}${suffix}</option>`;
              }).join('')}
            </select>
          </div>
          <span style="font-size: 0.66rem; font-weight: 700; color: #38bdf8; background: rgba(56, 189, 248, 0.14); border: 1px solid rgba(56, 189, 248, 0.3); padding: 2px 7px; border-radius: 999px; white-space: nowrap;">
            ${statusBadge}
          </span>
        </div>

        <!-- YTD / Annual Button + Clickable 12-Month Grid -->
        <div style="display: flex; flex-direction: column; gap: 5px; background: rgba(15, 23, 42, 0.55); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 7px; padding: 6px 8px;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 6px;">
            <span style="font-size: 0.67rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">Period</span>
            <button
              type="button"
              class="gw-rc-ytd-btn"
              data-mode="ytd"
              style="flex: 1; padding: 5px 8px; border-radius: 5px; font-size: 0.72rem; font-weight: 700; cursor: pointer; transition: all 0.15s ease; border: 1px solid ${timeMode === 'ytd' ? '#38bdf8' : 'rgba(255,255,255,0.12)'}; background: ${timeMode === 'ytd' ? 'rgba(56, 189, 248, 0.24)' : 'rgba(30, 41, 59, 0.7)'}; color: ${timeMode === 'ytd' ? '#f8fafc' : '#cbd5e1'};"
            >
              ${ytdButtonLabel}
            </button>
          </div>

          <div style="display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; margin-top: 2px;">
            ${RECENT_CLIMATE_MONTH_NAMES.map((mName, idx) => {
              const mNum = idx + 1;
              const isAvail = availMonths.has(mNum);
              const isSelected = timeMode === 'month' && month === mNum;
              return `
                <button
                  type="button"
                  class="gw-rc-month-btn"
                  data-month="${mNum}"
                  ${!isAvail ? 'disabled aria-disabled="true"' : ''}
                  title="${isAvail ? `${mName} ${year}` : `${mName} ${year} not yet available`}"
                  style="padding: 4px 2px; border-radius: 5px; font-size: 0.69rem; font-weight: 700; text-align: center; transition: all 0.14s ease; border: 1px solid ${isSelected ? '#38bdf8' : 'rgba(255,255,255,0.10)'}; background: ${isSelected ? 'rgba(56, 189, 248, 0.28)' : 'rgba(30, 41, 59, 0.65)'}; color: ${isSelected ? '#ffffff' : '#cbd5e1'}; cursor: ${isAvail ? 'pointer' : 'not-allowed'};"
                >
                  ${mName}
                </button>
              `;
            }).join('')}
          </div>
        </div>

        <!-- 7 Observed Climate Layers -->
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 0 2px;">
            <span style="font-size: 0.67rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em;">Observed Layer</span>
            <span style="font-size: 0.66rem; font-weight: 600; color: #cbd5e1;">${activeLayerTitle}</span>
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px;">
            ${RECENT_CLIMATE_METRICS.map((m, idx) => {
              const isActive = m.key === variable;
              const spanFull = idx === RECENT_CLIMATE_METRICS.length - 1 && RECENT_CLIMATE_METRICS.length % 2 === 1;
              return `
                <button
                  type="button"
                  class="gw-rc-var-btn"
                  data-variable="${m.key}"
                  title="${timeMode === 'month' ? m.monthDesc : (isComplete ? m.annualDesc : m.ytdDesc)}"
                  style="${spanFull ? 'grid-column: span 2;' : ''} display: flex; align-items: center; justify-content: space-between; gap: 5px; padding: 5px 7px; border-radius: 6px; font-size: 0.71rem; font-weight: 700; cursor: pointer; text-align: left; transition: all 0.14s ease; border: 1px solid ${isActive ? '#38bdf8' : 'rgba(255,255,255,0.09)'}; background: ${isActive ? 'rgba(56, 189, 248, 0.22)' : 'rgba(15, 23, 42, 0.6)'}; color: ${isActive ? '#ffffff' : '#cbd5e1'};"
                >
                  <span style="display: flex; align-items: center; gap: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    <span>${m.icon}</span>
                    <span>${m.shortLabel}</span>
                  </span>
                  ${m.conusOnly ? '<span style="font-size: 0.58rem; font-weight: 700; color: #94a3b8; background: rgba(255,255,255,0.07); padding: 1px 4px; border-radius: 3px;">CONUS</span>' : ''}
                </button>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Continuous Color Legend -->
        <div style="background: rgba(15, 23, 42, 0.72); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 6px; padding: 6px 9px; display: flex; flex-direction: column; gap: 4px;">
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.65rem; font-weight: 700; color: #94a3b8; text-transform: uppercase;">
            <span>${activeSpec.shortLabel}</span>
            <span>${legend.unit}</span>
          </div>
          <div style="height: 8px; width: 100%; border-radius: 4px; background: ${legend.gradient}; border: 1px solid rgba(255, 255, 255, 0.16);"></div>
          <div style="display: flex; justify-content: space-between; font-size: 0.63rem; font-weight: 700; color: #f8fafc;">
            ${legend.labels.map((lbl) => `<span>${lbl}</span>`).join('')}
          </div>
        </div>

        <!-- Data Attribution & Hover Hint -->
        <div style="font-size: 0.61rem; color: #64748b; line-height: 1.35; padding: 2px 3px 0; border-top: 1px solid rgba(255, 255, 255, 0.06); text-align: center;">
          NOAA NCEI nClimGrid (4.6 km CONUS) + NOAA CPC Global Unified (0.5°) + NOHRSC Snowfall • 1991–2020 Normals. Hover map for point almanac.
        </div>
      </div>
    `;
  }

  public bindEvents(container: HTMLElement): void {
    this.container = container;

    const yearSelect = container.querySelector('.gw-rc-year-select') as HTMLSelectElement | null;
    if (yearSelect) {
      yearSelect.addEventListener('change', (e) => {
        e.stopPropagation();
        const nextYear = parseInt(yearSelect.value, 10);
        if (Number.isNaN(nextYear)) return;
        const cur = store.getState();
        const resolved = RecentClimateService.resolveSelection(
          cur.manifest,
          nextYear,
          cur.recentClimateTimeMode,
          cur.recentClimateMonth,
          cur.recentClimateVariable
        );
        store.update({
          recentClimateYear: resolved.year,
          recentClimateMonth: resolved.month,
          recentClimateTimeMode: resolved.timeMode
        });
        this.updateUi();
      });
    }

    const ytdBtn = container.querySelector('.gw-rc-ytd-btn') as HTMLButtonElement | null;
    if (ytdBtn) {
      ytdBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        store.update({ recentClimateTimeMode: 'ytd' as RecentClimateTimeMode });
        this.updateUi();
      });
    }

    container.querySelectorAll('.gw-rc-month-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const el = btn as HTMLButtonElement;
        if (el.disabled) return;
        const mNum = parseInt(el.getAttribute('data-month') || '1', 10);
        if (Number.isNaN(mNum)) return;
        store.update({
          recentClimateTimeMode: 'month',
          recentClimateMonth: mNum
        });
        this.updateUi();
      });
    });

    container.querySelectorAll('.gw-rc-var-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const vKey = btn.getAttribute('data-variable') as RecentClimateVariable | null;
        if (!vKey) return;
        store.update({ recentClimateVariable: vKey });
        this.updateUi();
      });
    });
  }

  public updateUi(): void {
    let target: HTMLElement | null = null;
    if (this.container && document.body.contains(this.container)) {
      if (
        this.container.classList.contains('gw-inplace-card-body') ||
        this.container.classList.contains('legend-card-body')
      ) {
        target = this.container;
      } else {
        target = (this.container.querySelector('.gw-inplace-card-body') ||
          this.container.querySelector('.legend-card-body') ||
          this.container.querySelector('.gw-recent-climate-panel')?.parentElement) as HTMLElement | null;
      }
    }
    if (!target) {
      target = (document.querySelector('.gw-category-layer-row[data-layer="recent_climate"] .gw-inplace-card-body') ||
        document.querySelector('.legend-card[data-layer="recent_climate"] .legend-card-body') ||
        document.querySelector('.gw-recent-climate-panel')?.parentElement) as HTMLElement | null;
    }
    if (!target) return;
    this.container = target;
    const state = store.getState();
    target.innerHTML = this.render(state);
    this.bindEvents(target);
  }
}
